import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface Signup {
  signup_id: number;
  signed_up_at: string;
  signup_status: 'confirmed' | 'cancelled' | 'attended' | 'no_show';
  mow_day_id: number;
  event_date: string;
  mow_day_status: 'open' | 'closed' | 'completed' | 'cancelled';
  county: string | null;
  notes: string | null;
}

interface VolunteerSummary {
  id: number;
  status: 'pending' | 'vetted' | 'rejected' | 'inactive';
  name: string;
}

interface ApiData {
  volunteer: VolunteerSummary | null;
  signups: Signup[];
  hint?: string;
}

function fmtDateLong(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y) return iso;
  const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
  return dt.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function StatusBadge({ status }: { status: VolunteerSummary['status'] }) {
  const cls =
    status === 'vetted'   ? 'bg-forest text-cream' :
    status === 'pending'  ? 'bg-terracotta text-cream' :
    status === 'rejected' ? 'bg-terracotta/20 text-terracotta' :
                            'bg-forest/10 text-forest/60';
  const label =
    status === 'vetted'   ? 'Vetted Mowkin' :
    status === 'pending'  ? 'Pending vetting' :
    status === 'rejected' ? 'Rejected' :
                            'Inactive';
  return (
    <span className={`inline-block px-3 py-1 text-[11px] tracking-[0.15em] uppercase ${cls}`}>
      {label}
    </span>
  );
}

export function MyMowDaysPage() {
  const { user } = useAuth();
  const [data, setData] = useState<ApiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/my-mow-days', { credentials: 'include' });
        const json = (await res.json()) as
          | { ok: true; data: ApiData }
          | { ok: false; error: string };
        if (json.ok) setData(json.data);
        else setError(json.error);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Network error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const upcoming = (data?.signups || []).filter((s) => {
    return new Date(s.event_date + 'T00:00:00Z') >= new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z');
  });
  const past = (data?.signups || []).filter((s) => !upcoming.includes(s));

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
      <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-3">My Mow Days</div>
      <h1
        className="text-5xl text-forest mb-3 leading-tight font-display"
        style={{ fontVariationSettings: '"opsz" 144' }}
      >
        Where you're <em>showing up.</em>
      </h1>
      {user && (
        <p className="text-forest/70 mb-4">
          Signed in as <span className="text-forest font-medium">{user.email}</span>.
        </p>
      )}

      {loading && <p className="text-forest/60 italic">Loading…</p>}

      {!loading && error && (
        <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm">
          {error}
        </div>
      )}

      {!loading && data && !data.volunteer && (
        <div className="bg-cream border border-forest/15 p-8 mt-8">
          <h2 className="text-2xl text-forest mb-3 font-display">No volunteer application on file.</h2>
          <p className="text-forest/75 leading-relaxed mb-6">
            We don't have a Volunteer Mowkin application matching your account email. Apply first
            — once our team vets you, you can sign up for any open mow day.
          </p>
          <Link
            to="/volunteer"
            className="inline-block bg-terracotta text-cream px-6 py-3 text-sm tracking-[0.1em] uppercase hover:bg-forest transition-colors"
          >
            Apply to volunteer →
          </Link>
        </div>
      )}

      {!loading && data && data.volunteer && (
        <>
          <div className="flex items-center gap-3 mb-10">
            <StatusBadge status={data.volunteer.status} />
            <span className="text-forest/70 text-sm">{data.volunteer.name}</span>
          </div>

          {data.volunteer.status !== 'vetted' && (
            <div className="bg-sand/30 border-l-4 border-terracotta p-5 mb-10 text-sm leading-relaxed">
              {data.volunteer.status === 'pending' && (
                <>
                  <strong>Your application is being reviewed.</strong> Our team will reach out by
                  phone within 5 business days to vet you. Once vetted, you can sign up for mow days.
                </>
              )}
              {data.volunteer.status === 'rejected' && (
                <>Your application wasn't accepted. Reach out to our team if you'd like to discuss.</>
              )}
              {data.volunteer.status === 'inactive' && (
                <>Your volunteer status is currently inactive. Contact our team to reactivate.</>
              )}
            </div>
          )}

          {data.volunteer.status === 'vetted' && upcoming.length === 0 && past.length === 0 && (
            <div className="bg-cream border border-forest/15 p-8">
              <p className="text-forest/75 mb-6">
                You haven't signed up for any mow days yet.
              </p>
              <Link
                to="/mow-day"
                className="inline-block bg-terracotta text-cream px-6 py-3 text-sm tracking-[0.1em] uppercase hover:bg-forest transition-colors"
              >
                Pick a mow day →
              </Link>
            </div>
          )}

          {upcoming.length > 0 && (
            <section className="mb-12">
              <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-4">Upcoming</div>
              <div className="space-y-3">
                {upcoming.map((s) => (
                  <div key={s.signup_id} className="border border-forest/15 bg-cream p-5">
                    <div className="font-display text-xl text-forest mb-1">
                      {fmtDateLong(s.event_date)}
                    </div>
                    <div className="text-sm text-forest/65">
                      Signed up {new Date(s.signed_up_at).toLocaleDateString()}
                      {s.county ? ` · ${s.county}` : ''} · status {s.signup_status}
                    </div>
                    {s.notes && <div className="mt-2 text-sm text-forest/70 italic">{s.notes}</div>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {past.length > 0 && (
            <section>
              <div className="text-xs tracking-[0.25em] uppercase text-forest/60 mb-4">Past</div>
              <div className="space-y-3">
                {past.map((s) => (
                  <div key={s.signup_id} className="border border-forest/10 bg-cream/60 p-4 text-sm">
                    <div className="font-medium text-forest/80">{fmtDateLong(s.event_date)}</div>
                    <div className="text-forest/55 text-xs mt-1">
                      {s.signup_status}{s.county ? ` · ${s.county}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {data.volunteer.status === 'vetted' && (
            <div className="mt-12 pt-8 border-t border-forest/10">
              <Link
                to="/mow-day"
                className="inline-block bg-forest text-cream px-6 py-3 text-sm tracking-[0.1em] uppercase hover:bg-terracotta transition-colors"
              >
                Sign up for another mow day →
              </Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
